/**
 * Implementation Project — Instance types
 *
 * These are the runtime entities created when a template is stamped
 * onto a group. A project contains sections and tasks that track
 * the implementation workflow for a specific group/effective date.
 */

// =============================================================================
// Enums
// =============================================================================

/**
 * Task type — mirrors the Catalog-side enum (same Dataverse choice column).
 * Redefined here because Ops cannot import from the Catalog package.
 */
export const TaskType = {
  ACTION_ONLY: 100_000_000,
  DATA_BOUND: 100_000_001,
  DOCUMENT: 100_000_002,
} as const;
export type TaskTypeValue = (typeof TaskType)[keyof typeof TaskType];

export const TaskTypeLabels: Record<number, string> = {
  [TaskType.ACTION_ONLY]: 'Action Only',
  [TaskType.DATA_BOUND]: 'Data-Bound',
  [TaskType.DOCUMENT]: 'Document',
};

/**
 * Dependency type — same Dataverse choice as the template-side enum.
 */
export const DependencyType = {
  FINISH_TO_START: 100_000_000,
  START_TO_START: 100_000_001,
  FINISH_TO_FINISH: 100_000_002,
  START_TO_FINISH: 100_000_003,
} as const;
export type DependencyTypeValue = (typeof DependencyType)[keyof typeof DependencyType];

export const DependencyTypeLabels: Record<number, string> = {
  [DependencyType.FINISH_TO_START]: 'Finish → Start',
  [DependencyType.START_TO_START]: 'Start → Start',
  [DependencyType.FINISH_TO_FINISH]: 'Finish → Finish',
  [DependencyType.START_TO_FINISH]: 'Start → Finish',
};

/**
 * Transition type (decisions 12/13): what kind of transition this project is.
 * All three run on the same engine; the type drives stepper labels and
 * dashboard facets. Legacy rows were backfilled to Onboarding; the mapper
 * still defaults NULL to Onboarding because Dataverse column defaults do not
 * apply to API creates.
 */
/**
 * What kind of transition a project is. A COARSE PORTFOLIO FACET (Sasha,
 * 9/17): the solution-tagged sections carry the real detail, so a group with an
 * evergreen Rx line and a changing TPA line is ONE renewal and which sections
 * stamped is what expresses the difference.
 *
 * Consequence to know: the facet cannot answer questions about a specific line
 * of business. "Which groups still have unhandled Rx at renewal" comes from
 * per-solution transition coverage, not from here.
 *
 * SOLUTION_CHANGE carries the upsell case; there is no separate upsell value.
 */
export const TransitionType = {
  ONBOARDING: 100_000_000,
  SOLUTION_CHANGE: 100_000_001,
  TERMINATION: 100_000_002,
  /** Straight / evergreen: confirmed that nothing is changing. */
  RENEWAL: 100_000_003,
  RENEWAL_PLAN_CHANGE: 100_000_004,
  /**
   * Claimed, type not yet determined. The account exec opens a renewal BEFORE
   * knowing what it is, because they may be chasing an upsell or waiting on
   * plan decisions from the group or broker; kickoff stamps a triage section
   * and the real sections append once this resolves to one of the above.
   * Exists as its own value so "which renewals are untriaged" is one filter
   * rather than a task-level query.
   */
  RENEWAL_PENDING: 100_000_005,
} as const;
export type TransitionTypeValue = (typeof TransitionType)[keyof typeof TransitionType];

export const TransitionTypeLabels: Record<number, string> = {
  [TransitionType.ONBOARDING]: 'Onboarding',
  [TransitionType.SOLUTION_CHANGE]: 'Solution change',
  [TransitionType.TERMINATION]: 'Termination',
  [TransitionType.RENEWAL]: 'Renewal',
  [TransitionType.RENEWAL_PLAN_CHANGE]: 'Renewal with plan changes',
  [TransitionType.RENEWAL_PENDING]: 'Renewal pending',
};

/** The renewal family, for queue membership and portfolio facets. */
export const RENEWAL_TRANSITION_TYPES: readonly number[] = [
  TransitionType.RENEWAL,
  TransitionType.RENEWAL_PLAN_CHANGE,
  TransitionType.RENEWAL_PENDING,
];

export function isRenewalTransition(type: number | undefined): boolean {
  return type != null && RENEWAL_TRANSITION_TYPES.includes(type);
}

export const ProjectStatus = {
  ACTIVE: 100_000_000,
  COMPLETE: 100_000_001,
  CANCELLED: 100_000_002,
} as const;
export type ProjectStatusValue = (typeof ProjectStatus)[keyof typeof ProjectStatus];

export const ProjectStatusLabels: Record<number, string> = {
  [ProjectStatus.ACTIVE]: 'Active',
  [ProjectStatus.COMPLETE]: 'Complete',
  [ProjectStatus.CANCELLED]: 'Cancelled',
};

export const HealthStatus = {
  GREEN: 100_000_000,
  YELLOW: 100_000_001,
  RED: 100_000_002,
} as const;
export type HealthStatusValue = (typeof HealthStatus)[keyof typeof HealthStatus];

export const HealthStatusLabels: Record<number, string> = {
  [HealthStatus.GREEN]: 'Green',
  [HealthStatus.YELLOW]: 'Yellow',
  [HealthStatus.RED]: 'Red',
};

/** How critical a task is to go-live. Stamped from the template task. */
export const Criticality = {
  STANDARD: 100_000_000,
  IMPORTANT: 100_000_001,
  SHOWSTOPPER: 100_000_002,
} as const;
export type CriticalityValue = (typeof Criticality)[keyof typeof Criticality];

export const CriticalityLabels: Record<number, string> = {
  [Criticality.STANDARD]: 'Standard',
  [Criticality.IMPORTANT]: 'Important',
  [Criticality.SHOWSTOPPER]: 'Showstopper',
};

/**
 * Who a slip on this task hurts (ADO #1726, Lindsay 10/5). Stamped from the
 * template task, like criticality, and deliberately separate from it.
 * External = member affected (Rx joinder, signed BPD, eligibility).
 * Internal = Veracity operations affected (TPA plan document, ELAP / ASA
 * agreements holding up claims). Undefined = not yet classified.
 */
export const Impact = {
  INTERNAL: 100_000_000,
  EXTERNAL: 100_000_001,
  BOTH: 100_000_002,
} as const;
export type ImpactValue = (typeof Impact)[keyof typeof Impact];

export const ImpactLabels: Record<number, string> = {
  [Impact.BOTH]: 'Both',
  [Impact.EXTERNAL]: 'External',
  [Impact.INTERNAL]: 'Internal',
};

/** Pill text: "Both" alone reads as a non-answer next to a criticality pill. */
export const ImpactPillLabels: Record<number, string> = {
  [Impact.BOTH]: 'Internal + External',
  [Impact.EXTERNAL]: 'External',
  [Impact.INTERNAL]: 'Internal',
};

export const TaskStatus = {
  NOT_STARTED: 100_000_000,
  IN_PROGRESS: 100_000_001,
  SUBMITTED: 100_000_002,
  WAITING_CLIENT: 100_000_003,
  WAITING_BROKER: 100_000_004,
  WAITING_TPA: 100_000_005,
  WAITING_RX: 100_000_006,
  WAITING_VENDOR: 100_000_007,
  COMPLETE: 100_000_008,
  NOT_NEEDED: 100_000_009,
  BLOCKED: 100_000_010,
} as const;
export type TaskStatusValue = (typeof TaskStatus)[keyof typeof TaskStatus];

export const TaskStatusLabels: Record<number, string> = {
  [TaskStatus.NOT_STARTED]: 'Not Started',
  [TaskStatus.IN_PROGRESS]: 'In Progress',
  [TaskStatus.SUBMITTED]: 'Submitted',
  [TaskStatus.WAITING_CLIENT]: 'Waiting on Client/Broker',
  // WAITING_BROKER (100000004) is provisioned in Dataverse but hidden from the
  // UI — we show the same combined label so any legacy data still renders
  // correctly. Prefer WAITING_CLIENT for new assignments.
  [TaskStatus.WAITING_BROKER]: 'Waiting on Client/Broker',
  [TaskStatus.WAITING_TPA]: 'Waiting on TPA',
  [TaskStatus.WAITING_RX]: 'Waiting on Rx',
  [TaskStatus.WAITING_VENDOR]: 'Waiting on Vendor',
  [TaskStatus.COMPLETE]: 'Complete',
  [TaskStatus.NOT_NEEDED]: 'Not Needed',
  [TaskStatus.BLOCKED]: 'Blocked',
};

/** Task statuses that represent a "done" state (no further action needed) */
export const TERMINAL_TASK_STATUSES: ReadonlySet<number> = new Set([
  TaskStatus.COMPLETE,
  TaskStatus.NOT_NEEDED,
]);

// =============================================================================
// Core Types
// =============================================================================

export interface ImpProject {
  id: string;
  name: string;
  templateId?: string;
  templateVersion?: string;
  groupId: string;
  groupName?: string;
  coordinatorId?: string;
  coordinatorName?: string;
  startDate?: string;
  effectiveDate?: string;
  status: number;
  transitionType: TransitionTypeValue;
  /**
   * The plan year this transition renews FROM. Set on renewals so the renewal
   * queue can tell which renewal a transition belongs to; a group renewing
   * every year would otherwise look like one endless transition. Empty on
   * onboarding and termination.
   */
  contractYearId?: string;
  /**
   * Solution codes this transition covers, comma-separated in Dataverse and
   * split here. EMPTY means every solution the group has elected, which is the
   * normal case: one renewal transition per group per cycle. Populated when a
   * solution runs its own cycle (on-site health centers never align with a plan
   * year) and needs a transition of its own.
   */
  solutionScope?: string[];
  healthOverride?: number;
  healthStatus?: number;
  tasksTotal: number;
  tasksComplete: number;
  tasksOverdue: number;
  pctComplete: number;
  nextMilestoneName?: string;
  nextMilestoneDate?: string;
  sharePointFolderUrl?: string;
  sections?: ImpProjectSection[];
  rowVersion?: string;
  createdOn?: string;
  modifiedOn?: string;
}

export interface ImpProjectSection {
  id: string;
  name: string;
  sortOrder: number;
  projectId: string;
  sourceSectionId?: string;
  tasks?: ImpProjectTask[];
  rowVersion?: string;
}

export interface ImpProjectTask {
  id: string;
  name: string;
  taskType?: TaskTypeValue;
  instructions?: string;
  durationDays?: number;
  sortOrder: number;
  status: number;
  // Dates
  targetStart?: string;
  targetEnd?: string;
  dateSent?: string;
  dateReceived?: string;
  dateCompleted?: string;
  dateAcknowledged?: string;
  // Assignment
  assigneeRole?: string;
  assigneeId?: string;
  assigneeName?: string;
  contactId?: string;
  contactFreeText?: string;
  tags?: string;
  // Data binding
  dataBindingEntity?: string;
  dataBindingField?: string;
  autoCompleteOnData?: boolean;
  isGating?: boolean;
  criticality?: CriticalityValue;
  /** Internal / External / Both (ADO #1726), copied from the template task. */
  impact?: ImpactValue;
  /** Listed in the group status report email (ADO #1730), copied from the template task. */
  includeInStatusReport?: boolean;
  /**
   * T-minus (ADO #1661): calendar days before the project's effective date
   * this task targets. Stamped from the template task; undefined leaves the
   * task on CPM, dated from its duration and predecessors.
   */
  offsetDays?: number;
  /**
   * Target dates were set by hand, so a reschedule leaves them alone unless
   * the coordinator says otherwise. Cleared when the dates are re-derived.
   */
  dateOverridden?: boolean;
  // Notes (user-entered, free-form)
  notes?: string;
  // Document
  sharePointSubfolder?: string;
  sharePointUrl?: string;
  documentName?: string;
  // Email template (C2) — when set, the task row offers an Outlook draft
  emailTemplateId?: string;
  // Meeting template (C4) — when set, the task row offers an Outlook event
  meetingTemplateId?: string;
  // Hierarchy
  sectionId: string;
  projectId: string;
  sourceTaskId?: string;
  parentTaskId?: string;
  children?: ImpProjectTask[];
  // Audit
  lastUpdatedBy?: string;
  lastUpdatedOn?: string;
  rowVersion?: string;
  // Parent-project context — populated only by the My Tasks feed (listMyTasks
  // expands vba_Project). Undefined elsewhere.
  /** OpsGroup record id (route param for /ops/groups/:id). */
  groupId?: string;
  /** Group display name (falls back to the project name). */
  groupLabel?: string;
  /** Parent project's ProjectStatus value, for filtering out closed projects. */
  projectStatus?: number;
  /** Parent project's TransitionType (Onboarding / Solution change / Termination). */
  transitionType?: number;
  /** Parent project's effective (go-live) date. */
  projectEffectiveDate?: string;
  /** Parent project's HealthStatus (override applied), for an at-a-glance dot. */
  projectHealth?: number;
}

export interface ImpProjectDep {
  id: string;
  name: string;
  dependencyType?: number;
  lagDays?: number;
  predecessorId: string;
  successorId: string;
  rowVersion?: string;
}

// =============================================================================
// Input Types
// =============================================================================

export interface UpdateImpProjectInput {
  name?: string;
  status?: ProjectStatusValue;
  healthOverride?: number | null;
  startDate?: string | null;
  effectiveDate?: string | null;
  coordinatorId?: string | null;
  sharePointFolderUrl?: string | null;
  nextMilestoneName?: string | null;
  nextMilestoneDate?: string | null;
}

export interface UpdateImpProjectTaskInput {
  name?: string;
  status?: TaskStatusValue;
  targetStart?: string | null;
  targetEnd?: string | null;
  dateSent?: string | null;
  dateReceived?: string | null;
  dateCompleted?: string | null;
  dateAcknowledged?: string | null;
  assigneeRole?: string | null;
  assigneeId?: string | null;
  contactId?: string | null;
  contactFreeText?: string | null;
  instructions?: string | null;
  sharePointUrl?: string | null;
  notes?: string | null;
  /**
   * Explicit control of the hand-set flag. Left undefined, setting either
   * target date marks the task hand-set automatically, which is what a
   * coordinator editing a date in the UI means. Pass false to hand the task
   * back to the scheduler ("reset to calculated").
   */
  dateOverridden?: boolean;
  /** Catalog-side data binding. Null clears the binding entirely. */
  dataBindingEntity?: string | null;
  dataBindingField?: string | null;
}

/** Input for stamping a project from a template — defined here for type reference only */
export interface StampProjectInput {
  templateId: string;
  groupId: string;
  effectiveDate: string;
  transitionType: TransitionTypeValue;
  /** The plan year a renewal renews FROM. Omitted on onboarding/termination. */
  contractYearId?: string;
  /**
   * Solution codes this transition covers. Omit for the normal case (it covers
   * every solution the group has elected); set for the own-cycle exception.
   */
  solutionScope?: string[];
  /**
   * Library-section subset chosen at Start (decision 6: "they only want
   * TPA"). Undefined = stamp every template section. The C6 solution gate
   * still applies on top of this choice.
   */
  sectionIds?: string[];
  coordinatorId?: string;
  roleAssignments: Record<string, string>; // role name → systemuser ID
}

// =============================================================================
// Query Params
// =============================================================================

export interface ImpProjectQueryParams {
  groupId?: string;
  status?: ProjectStatusValue;
  coordinatorId?: string;
  orderBy?: 'effectiveDate' | 'startDate' | 'modifiedOn';
  orderDirection?: 'asc' | 'desc';
}

export interface ImpProjectTaskQueryParams {
  assigneeId?: string;
  status?: TaskStatusValue;
  isOverdue?: boolean;
  orderBy?: 'targetEnd' | 'sortOrder' | 'modifiedOn';
  orderDirection?: 'asc' | 'desc';
}
